export type LocalizedText = {
  id: string;
  en: string;
};

export type FAQItem = {
  q: LocalizedText;
  a: LocalizedText;
};

export type Product = {
  id: string;
  slug: string;
  badges?: string[];
  featured?: boolean;
  title: LocalizedText;
  tagline: LocalizedText;
  priceLabel?: LocalizedText;
  priceMainLabel: LocalizedText;
  priceStrikeLabel?: LocalizedText;
  basePriceValue?: number;
  promoPriceValue: number;
  durationLabel?: LocalizedText;
  formatLabel?: LocalizedText;
  highlights: LocalizedText[];
  faq: FAQItem[];
  rules: LocalizedText[];
  whatsappMessage: LocalizedText;
};

export const PRODUCTS: Product[] = [
  {
    id: "ourdo-buddy-session",
    slug: "ourdo-buddy-session",
    badges: ["HOT", "NEW"],
    featured: true,
    title: {
      id: "Ourdo Buddy Session",
      en: "Ourdo Buddy Session",
    },
    tagline: {
      id: "Sesi 1-on-1 untuk bikin masalah teknis kamu jelas dan langsung bisa dieksekusi.",
      en: "A 1-on-1 session to clarify your technical problem and turn it into executable next steps.",
    },
    priceLabel: {
      id: "Rp150.000",
      en: "IDR 150k",
    },
    priceMainLabel: {
      id: "Rp150.000",
      en: "IDR 150k",
    },
    priceStrikeLabel: {
      id: "Rp350.000",
      en: "IDR 350k",
    },
    basePriceValue: 350000,
    promoPriceValue: 150000,
    durationLabel: {
      id: "60 menit",
      en: "60 minutes",
    },
    formatLabel: {
      id: "Online",
      en: "Online",
    },
    highlights: [
      {
        id: "Diskusi 1-on-1 (60 menit)",
        en: "1-on-1 discussion (60 minutes)",
      },
      {
        id: "Arah yang jelas untuk masalah kamu",
        en: "Clear direction for your problem",
      },
      {
        id: "Next step yang bisa langsung kamu kerjain setelah sesi",
        en: "Actionable next steps you can execute right away",
      },
    ],
    faq: [
      {
        q: {
          id: "Ini cocok untuk siapa?",
          en: "Who is this session for?",
        },
        a: {
          id: "Cocok untuk kamu yang mau masuk dunia IT tapi masih minder, baru masuk kerja dan masih bingung dengan code & cara kerja tim, lagi shifting role (BE ↔ FE / jadi fullstack), atau mau persiapan interview dan butuh arahan yang jelas.",
          en: "Great for those entering IT, new to the job and still figuring out code and team workflows, switching roles (BE ↔ FE / fullstack), or preparing for interviews and needing clear direction.",
        },
      },
      {
        q: {
          id: "Bedanya dengan kelas/bootcamp?",
          en: "How is this different from classes/bootcamps?",
        },
        a: {
          id: "Ini sesi private 1-on-1 dan pembahasannya disesuaikan dengan kebutuhan kamu, bukan materi umum seperti kelas/bootcamp.",
          en: "This is a private 1-on-1 session tailored to your needs, not a general curriculum like a class or bootcamp.",
        },
      },
      {
        q: {
          id: "Bisa bahas PR/code?",
          en: "Can we discuss PR/code?",
        },
        a: {
          id: "Bisa. Kamu boleh bawa PR, potongan code, atau masalah logic yang lagi kamu hadapi.",
          en: "Yes. Bring PRs, code snippets, or logic issues you are facing.",
        },
      },
      {
        q: {
          id: "Online atau offline?",
          en: "Online or offline?",
        },
        a: {
          id: "Untuk sekarang online.",
          en: "Online for now.",
        },
      },
      {
        q: {
          id: "Apa yang harus dipersiapkan?",
          en: "What should I prepare?",
        },
        a: {
          id: "Minimal siapin laptop. Untuk detail bahan yang perlu dibawa (misal jobdesc, repo, PR, atau pertanyaan), kita bahas dulu di diskusi awal supaya sesi 60 menitnya efektif.",
          en: "At minimum, prepare a laptop. For detailed materials (jobdesc, repo, PRs, or questions), we will align in the initial discussion so the 60-minute session is effective.",
        },
      },
      {
        q: {
          id: "Reschedule/cancel gimana?",
          en: "How do reschedule/cancel work?",
        },
        a: {
          id: "Bisa chat admin untuk reschedule/cancel, maksimal H-3 sebelum jadwal sesi.",
          en: "Chat the admin to reschedule/cancel at least 3 days before the session.",
        },
      },
      {
        q: {
          id: "Boleh bahas hal kantor?",
          en: "Can we discuss work issues?",
        },
        a: {
          id: "Boleh bahas problem kerja secara umum (pattern, approach, cara berpikir). Tapi jangan share data sensitif/rahasia perusahaan.",
          en: "We can discuss general work problems (patterns, approach, ways of thinking). Do not share sensitive or confidential company data.",
        },
      },
    ],
    rules: [
      {
        id: "Reschedule/cancel maksimal H-3 sebelum jadwal.",
        en: "Reschedule/cancel at least 3 days before the schedule.",
      },
      {
        id: "Boleh bahas problem kerja secara umum, tapi jangan share data sensitif/rahasia perusahaan.",
        en: "General work problems are fine, but do not share sensitive/confidential company data.",
      },
    ],
    whatsappMessage: {
      id: "Halo admin Ourdo, saya mau Ourdo Buddy Session (60 menit). Topik saya: ____",
      en: "Hi Ourdo admin, I want an Ourdo Buddy Session (60 minutes). My topic is: ____",
    },
  },
  {
    id: "private-backend",
    slug: "private-backend",
    badges: ["HOT"],
    featured: true,
    title: {
      id: "Private Backend (BE)",
      en: "Private Backend (BE)",
    },
    tagline: {
      id: "Belajar backend terstruktur sampai siap bikin API.",
      en: "Structured backend learning until you are ready to build APIs.",
    },
    priceMainLabel: {
      id: "Rp200.000",
      en: "IDR 200k",
    },
    priceStrikeLabel: {
      id: "Rp350.000",
      en: "IDR 350k",
    },
    basePriceValue: 350000,
    promoPriceValue: 200000,
    durationLabel: {
      id: "60-90 menit",
      en: "60-90 minutes",
    },
    formatLabel: {
      id: "Online",
      en: "Online",
    },
    highlights: [],
    faq: [],
    rules: [],
    whatsappMessage: {
      id: "Halo admin Ourdo, saya tertarik Private Backend (BE).",
      en: "Hi Ourdo admin, I'm interested in Private Backend (BE).",
    },
  },
  {
    id: "private-frontend",
    slug: "private-frontend",
    badges: ["HOT"],
    featured: true,
    title: {
      id: "Private Frontend (FE)",
      en: "Private Frontend (FE)",
    },
    tagline: {
      id: "Belajar frontend sampai bisa bikin React app siap demo.",
      en: "Learn frontend until you can ship a demo-ready React app.",
    },
    priceMainLabel: {
      id: "Rp200.000",
      en: "IDR 200k",
    },
    priceStrikeLabel: {
      id: "Rp350.000",
      en: "IDR 350k",
    },
    basePriceValue: 350000,
    promoPriceValue: 200000,
    durationLabel: {
      id: "60-90 menit",
      en: "60-90 minutes",
    },
    formatLabel: {
      id: "Online",
      en: "Online",
    },
    highlights: [],
    faq: [],
    rules: [],
    whatsappMessage: {
      id: "Halo admin Ourdo, saya tertarik Private Frontend (FE).",
      en: "Hi Ourdo admin, I'm interested in Private Frontend (FE).",
    },
  },
  {
    id: "private-sd",
    slug: "private-sd",
    badges: ["NEW"],
    featured: true,
    title: {
      id: "Private Anak SD",
      en: "Private Class for Elementary",
    },
    tagline: {
      id: "Belajar logika coding + web basic yang fun untuk anak SD.",
      en: "Fun coding logic and web basics for elementary students.",
    },
    priceMainLabel: {
      id: "Rp200.000",
      en: "IDR 200k",
    },
    priceStrikeLabel: {
      id: "Rp250.000",
      en: "IDR 250k",
    },
    basePriceValue: 250000,
    promoPriceValue: 200000,
    durationLabel: {
      id: "60-90 menit",
      en: "60-90 minutes",
    },
    formatLabel: {
      id: "Online",
      en: "Online",
    },
    highlights: [],
    faq: [],
    rules: [],
    whatsappMessage: {
      id: "Halo admin Ourdo, saya tertarik Private Anak SD.",
      en: "Hi Ourdo admin, I'm interested in Private Class for Elementary.",
    },
  },
  {
    id: "private-smp",
    slug: "private-smp",
    badges: ["NEW"],
    featured: true,
    title: {
      id: "Private Anak SMP",
      en: "Private Class for Middle School",
    },
    tagline: {
      id: "Belajar problem solving + web basic untuk anak SMP.",
      en: "Problem solving and web basics for middle school students.",
    },
    priceMainLabel: {
      id: "Rp200.000",
      en: "IDR 200k",
    },
    priceStrikeLabel: {
      id: "Rp250.000",
      en: "IDR 250k",
    },
    basePriceValue: 250000,
    promoPriceValue: 200000,
    durationLabel: {
      id: "60-90 menit",
      en: "60-90 minutes",
    },
    formatLabel: {
      id: "Online",
      en: "Online",
    },
    highlights: [],
    faq: [],
    rules: [],
    whatsappMessage: {
      id: "Halo admin Ourdo, saya tertarik Private Anak SMP.",
      en: "Hi Ourdo admin, I'm interested in Private Class for Middle School.",
    },
  },
];

export const getProductBySlug = (slug: string): Product | undefined =>
  PRODUCTS.find((product) => product.slug === slug);
